import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  areAssignedIdSetsEqual,
  buildMyLearningRoleGroups,
  computeMyLearningAssignedIds,
  getRoleAssignmentState,
  getStaffAdditionalTrainingRoleNames,
  initMyLearningAssignmentDraft,
  QMS_TRAINING_ROLE_SUBTITLE,
  sortRoleGroupsAssignedFirst,
  sortStaffAssignedFirst,
  staffMatchesMyLearningRole,
} from "./qmsPublishStaffUtils";

const EMPTY_TRAINING_ROLES = Object.freeze([]);
const VIEW_BY_ROLE = "role";
const VIEW_ALL_STAFF = "staff";

function RoleCheckbox({ checked, indeterminate, disabled, onChange, ariaLabel }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={onChange}
    />
  );
}

export function MyLearningSaveBar({
  assignedCount = 0,
  isDirty = false,
  isSaving = false,
  onSave,
  className = "",
}) {
  return (
    <div className={`qms-publish-ml-staff__save-bar${className ? ` ${className}` : ""}`}>
      <div className="qms-publish-ml-staff__save-copy">
        <strong>{assignedCount}</strong> staff selected
        {isSaving ? (
          <span className="qms-publish-ml-staff__save-pending">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            Saving…
          </span>
        ) : isDirty ? (
          <span className="qms-publish-ml-staff__save-unsaved">Unsaved changes</span>
        ) : (
          <span className="qms-publish-ml-staff__save-saved">Saved</span>
        )}
      </div>
      <div className="qms-publish-ml-staff__save-actions">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!isDirty || isSaving}
          onClick={onSave}
        >
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
              Saving…
            </>
          ) : (
            <>
              <i className="fas fa-save me-1" aria-hidden="true" />
              Save
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function QmsMyLearningAssignmentPanel({
  staff = [],
  documentId,
  staffTrainingAdditionalRoles = EMPTY_TRAINING_ROLES,
  loading = false,
  saving = false,
  onSave,
  onDirtyChange,
  savePlacement = "inline",
  onSaveBarStateChange,
  className = "qms-publish-ml-staff",
}) {
  const [viewMode, setViewMode] = useState(VIEW_BY_ROLE);
  const [search, setSearch] = useState("");
  const [expandedRoleIds, setExpandedRoleIds] = useState(() => new Set());
  const [draft, setDraft] = useState(() =>
    initMyLearningAssignmentDraft(staff, staffTrainingAdditionalRoles),
  );
  const [savedDraft, setSavedDraft] = useState(() =>
    initMyLearningAssignmentDraft(staff, staffTrainingAdditionalRoles),
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  useEffect(() => {
    setSearch("");
    setViewMode(VIEW_BY_ROLE);
    setExpandedRoleIds(new Set());
  }, [documentId]);

  useEffect(() => {
    const next = initMyLearningAssignmentDraft(staff, staffTrainingAdditionalRoles);
    setDraft(next);
    setSavedDraft(next);
    setIsSavingDraft(false);

    const roleGroups = buildMyLearningRoleGroups(staff, staffTrainingAdditionalRoles);
    const assignedIds = computeMyLearningAssignedIds(
      staff,
      next.selectedRoleIds,
      next.individualStaffIds,
      next.excludedStaffIds,
      staffTrainingAdditionalRoles,
    );
    const openIds = new Set(
      roleGroups
        .filter((group) =>
          (group.staff || []).some((row) => assignedIds.has(String(row.StaffId))),
        )
        .map((group) => String(group.roleId)),
    );
    setExpandedRoleIds(openIds);
  }, [documentId, staff, staffTrainingAdditionalRoles]);

  const assignedIds = useMemo(
    () =>
      computeMyLearningAssignedIds(
        staff,
        draft.selectedRoleIds,
        draft.individualStaffIds,
        draft.excludedStaffIds,
        staffTrainingAdditionalRoles,
      ),
    [staff, draft, staffTrainingAdditionalRoles],
  );

  const roleGroups = useMemo(() => {
    const groups = buildMyLearningRoleGroups(staff, staffTrainingAdditionalRoles).map((group) => {
      const roleStaff = (group.staff || []).map((row) => ({
        ...row,
        Assigned: assignedIds.has(String(row.StaffId)) ? 1 : 0,
      }));
      return {
        ...group,
        staff: roleStaff,
        state: getRoleAssignmentState(roleStaff),
      };
    });
    return sortRoleGroupsAssignedFirst(groups);
  }, [staff, assignedIds, staffTrainingAdditionalRoles]);

  const searchQuery = search.trim().toLowerCase();

  const filteredRoleGroups = useMemo(() => {
    if (!searchQuery) return roleGroups;

    return roleGroups
      .map((group) => {
        const roleMatches = String(group.roleName || "")
          .toLowerCase()
          .includes(searchQuery);
        const matchingStaff = (group.staff || []).filter((row) =>
          String(row.StaffName || row.UserName || "")
            .toLowerCase()
            .includes(searchQuery),
        );

        if (roleMatches) return group;
        if (matchingStaff.length) {
          return { ...group, staff: matchingStaff, isSearchSubset: true };
        }
        return null;
      })
      .filter(Boolean);
  }, [roleGroups, searchQuery]);

  const sortedStaff = useMemo(
    () =>
      sortStaffAssignedFirst(staff).map((row) => ({
        ...row,
        Assigned: assignedIds.has(String(row.StaffId)) ? 1 : 0,
      })),
    [staff, assignedIds],
  );

  const filteredStaff = useMemo(() => {
    if (!searchQuery) return sortedStaff;
    return sortedStaff.filter((row) => {
      return (
        String(row.StaffName || row.UserName || "")
          .toLowerCase()
          .includes(searchQuery) ||
        String(row.Roles || "")
          .toLowerCase()
          .includes(searchQuery)
      );
    });
  }, [sortedStaff, searchQuery]);

  useEffect(() => {
    if (!searchQuery || viewMode !== VIEW_BY_ROLE) return;
    setExpandedRoleIds((current) => {
      const next = new Set(current);
      filteredRoleGroups.forEach((group) => next.add(String(group.roleId)));
      return next;
    });
  }, [filteredRoleGroups, searchQuery, viewMode]);

  const isSaving = saving || isSavingDraft;
  const isDirty =
    !areAssignedIdSetsEqual(draft.selectedRoleIds, savedDraft.selectedRoleIds) ||
    !areAssignedIdSetsEqual(draft.individualStaffIds, savedDraft.individualStaffIds) ||
    !areAssignedIdSetsEqual(draft.excludedStaffIds, savedDraft.excludedStaffIds);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const toggleRole = useCallback(
    (roleId, checked) => {
      const id = String(roleId || "");
      if (!id || isSaving) return;

      setDraft((current) => {
        const next = {
          selectedRoleIds: new Set(current.selectedRoleIds),
          individualStaffIds: new Set(current.individualStaffIds),
          excludedStaffIds: new Set(current.excludedStaffIds),
        };

        if (checked) {
          next.selectedRoleIds.add(id);
          (staff || []).forEach((row) => {
            if (staffMatchesMyLearningRole(row, id, staffTrainingAdditionalRoles)) {
              next.excludedStaffIds.delete(String(row.StaffId || ""));
            }
          });
        } else {
          next.selectedRoleIds.delete(id);
          (staff || []).forEach((row) => {
            if (!staffMatchesMyLearningRole(row, id, staffTrainingAdditionalRoles)) return;

            const staffId = String(row.StaffId || "");
            if (!staffId) return;

            next.individualStaffIds.delete(staffId);

            const stillCoveredByRole = [...next.selectedRoleIds].some((otherRoleId) =>
              staffMatchesMyLearningRole(row, otherRoleId, staffTrainingAdditionalRoles),
            );

            if (stillCoveredByRole) {
              next.excludedStaffIds.delete(staffId);
            } else {
              next.excludedStaffIds.add(staffId);
            }
          });
        }

        return next;
      });

      if (checked) {
        setExpandedRoleIds((current) => new Set(current).add(id));
      }
    },
    [isSaving, staff, staffTrainingAdditionalRoles],
  );

  const toggleStaff = useCallback(
    (staffRow, assign) => {
      const staffId = String(staffRow?.StaffId || "");
      if (!staffId || isSaving) return;

      setDraft((current) => {
        const next = {
          selectedRoleIds: new Set(current.selectedRoleIds),
          individualStaffIds: new Set(current.individualStaffIds),
          excludedStaffIds: new Set(current.excludedStaffIds),
        };

        if (assign) {
          next.individualStaffIds.add(staffId);
          next.excludedStaffIds.delete(staffId);
        } else if (next.individualStaffIds.has(staffId)) {
          next.individualStaffIds.delete(staffId);
        } else {
          next.excludedStaffIds.add(staffId);
        }

        return next;
      });
    },
    [isSaving],
  );

  const handleSave = useCallback(async () => {
    if (!isDirty || isSaving) return;

    setIsSavingDraft(true);
    try {
      await onSave?.(draft);
      setSavedDraft({
        selectedRoleIds: new Set(draft.selectedRoleIds),
        individualStaffIds: new Set(draft.individualStaffIds),
        excludedStaffIds: new Set(draft.excludedStaffIds),
      });
    } catch {
      // Keep draft so the user can retry.
    } finally {
      setIsSavingDraft(false);
    }
  }, [draft, isDirty, isSaving, onSave]);

  const toggleRoleExpanded = useCallback((roleId) => {
    const id = String(roleId || "");
    setExpandedRoleIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAllRoles = useCallback(() => {
    setExpandedRoleIds(new Set(filteredRoleGroups.map((group) => String(group.roleId))));
  }, [filteredRoleGroups]);

  const collapseAllRoles = useCallback(() => {
    setExpandedRoleIds(new Set());
  }, []);

  const fullRoleCount = roleGroups.filter((group) => group.state.checked).length;
  const partialRoleCount = roleGroups.filter((group) => group.state.indeterminate).length;

  useEffect(() => {
    if (savePlacement !== "footer") {
      onSaveBarStateChange?.(null);
      return;
    }

    onSaveBarStateChange?.({
      assignedCount: assignedIds.size,
      isDirty,
      isSaving,
      onSave: handleSave,
    });
  }, [
    assignedIds.size,
    handleSave,
    isDirty,
    isSaving,
    onSaveBarStateChange,
    savePlacement,
  ]);

  const panelClassName = [
    className,
    savePlacement === "footer" ? "qms-publish-ml-staff--footer-save" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={panelClassName}>
      <div className="qms-publish-ml-staff__header">
        <p className="qms-publish-ml-staff__hint text-muted mb-0">
          Tick a whole role to assign everyone in it, or open a role and pick individual staff.
          Click <strong>Save</strong> when finished.
        </p>

        <div className="qms-publish-ml-staff__header-bar">
          <div className="qms-publish-ml-staff__header-group qms-publish-ml-staff__header-group--view">
            <span className="qms-publish-ml-staff__header-label">View</span>
            <div className="qms-publish-ml-staff__modes" role="tablist" aria-label="Assignment view">
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === VIEW_BY_ROLE}
                className={`qms-publish-ml-staff__mode${viewMode === VIEW_BY_ROLE ? " is-active" : ""}`}
                onClick={() => setViewMode(VIEW_BY_ROLE)}
              >
                <i className="fas fa-users-cog" aria-hidden="true" />
                By role
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === VIEW_ALL_STAFF}
                className={`qms-publish-ml-staff__mode${viewMode === VIEW_ALL_STAFF ? " is-active" : ""}`}
                onClick={() => setViewMode(VIEW_ALL_STAFF)}
              >
                <i className="fas fa-user-check" aria-hidden="true" />
                All staff
              </button>
            </div>
          </div>

          <div className="qms-publish-ml-staff__header-group qms-publish-ml-staff__header-group--search">
            <span className="qms-publish-ml-staff__header-label">Search</span>
            <div className="qms-publish-ml-staff__header-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder={
                  viewMode === VIEW_BY_ROLE
                    ? "Roles or staff…"
                    : "Staff name or role…"
                }
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          {viewMode === VIEW_BY_ROLE ? (
            <div className="qms-publish-ml-staff__header-group qms-publish-ml-staff__header-group--list">
              <span className="qms-publish-ml-staff__header-label">List</span>
              <div className="qms-publish-ml-staff__header-list-actions">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={expandAllRoles}>
                  Expand all
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={collapseAllRoles}>
                  Collapse all
                </button>
              </div>
            </div>
          ) : null}

          <div className="qms-publish-ml-staff__header-group qms-publish-ml-staff__header-group--stats">
            <span className="qms-publish-ml-staff__header-label">Selection</span>
            <div className="qms-publish-ml-staff__header-stats">
              <span className="qms-publish-ml-staff__header-stat">
                <strong>{assignedIds.size}</strong>
                <em>staff</em>
              </span>
              {fullRoleCount ? (
                <span className="qms-publish-ml-staff__header-stat">
                  <strong>{fullRoleCount}</strong>
                  <em>full roles</em>
                </span>
              ) : null}
              {partialRoleCount ? (
                <span className="qms-publish-ml-staff__header-stat is-partial">
                  <strong>{partialRoleCount}</strong>
                  <em>partial</em>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="qms-publish-ml-staff__panel">
        {loading ? (
          <div className="qms-doc-lib__staff-empty">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Loading assignment list…</span>
          </div>
        ) : viewMode === VIEW_BY_ROLE ? (
          filteredRoleGroups.length === 0 ? (
            <div className="qms-doc-lib__staff-empty">
              <span>No roles or staff match your search.</span>
            </div>
          ) : (
            <div className="qms-publish-ml-staff__accordion">
              {filteredRoleGroups.map((group) => {
                const roleId = String(group.roleId || "");
                const isExpanded = expandedRoleIds.has(roleId);
                const { state } = group;
                const groupClass = [
                  state.checked ? "is-full" : "",
                  state.indeterminate ? "is-partial" : "",
                  isExpanded ? "is-open" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <section
                    key={roleId}
                    className={`qms-publish-ml-staff__accordion-group${groupClass ? ` ${groupClass}` : ""}`}
                  >
                    <div className="qms-publish-ml-staff__accordion-head">
                      <button
                        type="button"
                        className="qms-publish-ml-staff__accordion-toggle"
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.roleName}`}
                        onClick={() => toggleRoleExpanded(roleId)}
                      >
                        <i
                          className={`fas fa-chevron-${isExpanded ? "down" : "right"}`}
                          aria-hidden="true"
                        />
                      </button>

                      <label className="qms-publish-ml-staff__accordion-check">
                        <RoleCheckbox
                          checked={state.checked}
                          indeterminate={state.indeterminate}
                          disabled={isSaving}
                          ariaLabel={`Assign all ${group.roleName} staff`}
                          onChange={(event) => toggleRole(roleId, event.target.checked)}
                        />
                      </label>

                      <button
                        type="button"
                        className="qms-publish-ml-staff__accordion-title"
                        onClick={() => toggleRoleExpanded(roleId)}
                      >
                        <strong>{group.roleName}</strong>
                        <span>
                          {state.assignedCount} of {group.staff.length} selected
                        </span>
                      </button>
                    </div>

                    <div
                      className={`qms-publish-ml-staff__accordion-body-wrap${
                        isExpanded ? " is-open" : ""
                      }`}
                      hidden={!isExpanded}
                    >
                      <ul className="qms-publish-ml-staff__accordion-body">
                        {group.staff.map((row) => {
                          const staffId = String(row.StaffId || "");
                          const isAssigned = assignedIds.has(staffId);

                          return (
                            <li
                              key={`${roleId}-${staffId}`}
                              className={`qms-publish-ml-staff__accordion-staff${
                                isAssigned ? " is-assigned" : ""
                              }`}
                            >
                              <label>
                                <input
                                  type="checkbox"
                                  checked={isAssigned}
                                  disabled={isSaving}
                                  onChange={(event) => toggleStaff(row, event.target.checked)}
                                />
                                <span className="qms-publish-ml-staff__accordion-staff-copy">
                                  <span className="qms-publish-ml-staff__accordion-staff-name">
                                    {row.StaffName || row.UserName || "Staff"}
                                  </span>
                                  {row.isTrainingRoleMembership ? (
                                    <small className="qms-publish-ml-staff__accordion-staff-meta qms-publish-ml-staff__accordion-staff-meta--training">
                                      {QMS_TRAINING_ROLE_SUBTITLE}
                                    </small>
                                  ) : null}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </section>
                );
              })}
            </div>
          )
        ) : filteredStaff.length === 0 ? (
          <div className="qms-doc-lib__staff-empty">
            <span>No staff match your search.</span>
          </div>
        ) : (
          <ul className="qms-publish-ml-staff__flat-list">
            {filteredStaff.map((row) => {
              const staffId = String(row.StaffId || "");
              const isAssigned = assignedIds.has(staffId);
              const trainingRoleNames = getStaffAdditionalTrainingRoleNames(
                row,
                staffTrainingAdditionalRoles,
              );

              return (
                <li
                  key={staffId}
                  className={`qms-publish-ml-staff__flat-row${isAssigned ? " is-assigned" : ""}`}
                >
                  <label>
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      disabled={isSaving}
                      onChange={(event) => toggleStaff(row, event.target.checked)}
                    />
                    <span className="qms-publish-ml-staff__flat-copy">
                      <strong>{row.StaffName || row.UserName || "Staff"}</strong>
                      <small>{row.Roles || "—"}</small>
                      {trainingRoleNames.length ? (
                        <small className="qms-publish-ml-staff__flat-training">
                          {trainingRoleNames.join(", ")} {QMS_TRAINING_ROLE_SUBTITLE}
                        </small>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {savePlacement === "inline" ? (
        <MyLearningSaveBar
          assignedCount={assignedIds.size}
          isDirty={isDirty}
          isSaving={isSaving}
          onSave={handleSave}
        />
      ) : null}
    </div>
  );
}

export default QmsMyLearningAssignmentPanel;
